
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import { useHash } from "@/contexts/HashContext";

const FaqSection = () => {
  const schemaRef = useRef<HTMLScriptElement>(null);
  const { currentHash } = useHash();
  const [openItems, setOpenItems] = useState<string[]>([]);
  const sectionRef = useRef<HTMLElement>(null);

  const faqData = [
    {
      id: "faq-1",
      question: "Was ist SPTools?",
      answer: "SPTools ist eine Middleware, die TYPO3 CMS mit Microsoft 365-Diensten wie SharePoint Online, Teams und Exchange verbindet. Sie ermöglicht eine nahtlose Integration beider Systeme, um Arbeitsabläufe zu optimieren und die Effizienz zu steigern."
    },
    {
      id: "faq-2",
      question: "Welche TYPO3-Versionen werden unterstützt?",
      answer: "SPTools ist kompatibel mit TYPO3 Version 9 bis 13."
    },
    {
      id: "faq-3",
      question: "Welche Microsoft 365-Dienste lassen sich integrieren?",
      answer: "SPTools ermöglicht die Integration verschiedener Microsoft 365-Dienste, darunter SharePoint, Teams, OneDrive und Outlook. Dadurch können Inhalte und Funktionen dieser Dienste direkt in Ihre TYPO3-Website eingebunden werden."
    },
    {
      id: "faq-4",
      question: "Wie funktioniert die Online-Terminbuchung mit SPTools?",
      answer: "Besucher können über ein Formular auf Ihrer TYPO3-Website Termine buchen. Die Daten werden automatisch in den Outlook-Kalender übertragen, und es wird ein Microsoft Teams-Meeting erstellt. Bestätigungen und Erinnerungen werden automatisiert versendet."
    },
    {
      id: "faq-5",
      question: "Wie funktioniert die Dokumentenfreigabe mit SPTools?",
      answer: "Dokumente, die über TYPO3 hochgeladen werden, können automatisch in SharePoint gespeichert und durch vordefinierte Workflows zur Freigabe an zuständige Mitarbeiter weitergeleitet werden."
    },
    {
      id: "faq-6",
      question: "Wie unterstützt SPTools den Bewerbungsprozess?",
      answer: "Bewerberdaten, die über TYPO3 eingegeben werden, werden automatisch in SharePoint übertragen. Das HR-Team wird über Workflows benachrichtigt, kann Bewerbungen direkt in SharePoint verwalten und Vorstellungsgespräche über Microsoft Teams koordinieren."
    },
    {
      id: "faq-7",
      question: "Wie sicher ist die Speicherung von Bewerberdaten?",
      answer: "Alle Bewerberdaten werden zentral und sicher in SharePoint gespeichert, was eine strukturierte Verwaltung und den Schutz sensibler Informationen gewährleistet."
    },
    {
      id: "faq-8",
      question: "Wer übernimmt die Implementierung von SPTools?",
      answer: "Die Implementierung kann durch Ihr internes IT-Team erfolgen oder durch spezialisierte Dienstleister, die Erfahrung mit TYPO3 und Microsoft 365-Integrationen haben. Gerne übernehmen wir für Sie die komplette Implementierung und Konfiguration nach Ihren Vorgaben."
    },
    {
      id: "faq-9",
      question: "Wie erfolgt die Integration von SPTools in unsere bestehende TYPO3-Website?",
      answer: "Die Integration von SPTools in TYPO3 erfolgt durch die Installation der SPTools Core Extension (kompatibel mit TYPO3 Version 11 und 13) sowie des Ausgabe-Plugins. Anschließend wird das Plugin konfiguriert, um Inhalte aus Microsoft 365, wie SharePoint-Dokumente, nahtlos in Ihre TYPO3-Website einzubinden."
    },
    {
      id: "faq-10",
      question: "Ist SPTools DSGVO-konform?",
      answer: "Ja, SPTools wurde unter Berücksichtigung der Datenschutz-Grundverordnung (DSGVO) entwickelt. Es werden nur autorisierte Datenquellen eingebunden, und die Verarbeitung personenbezogener Daten erfolgt gemäß den geltenden Datenschutzbestimmungen."
    },
    {
      id: "faq-11",
      question: "Welche Lizenzmodelle bietet SPTools an?",
      answer: "SPTools bietet zwei Lizenzmodelle an:<br><br><ul class='list-disc pl-5'><li>Single-Domain-Lizenz: Für die Integration in eine TYPO3-Website mit einem Microsoft 365-Tenant.</li><br><li>Multi-Domain-Lizenz: Für die Integration in mehrere TYPO3-Websites mit einem Microsoft 365-Tenant.</li></ul>"
    },
    {
      id: "faq-12",
      question: "Wie lange dauert die Implementierung von SPTools?",
      answer: "Die Implementierung von SPTools dauert in der Regel 1–2 Tage, abhängig von den spezifischen Anforderungen und der Komplexität Ihrer TYPO3-Website."
    },
    {
      id: "faq-13",
      question: "Bietet CCDS Unterstützung bei der Implementierung und Konfiguration von SPTools?",
      answer: "Ja, CCDS übernimmt gerne die Implementierung und Konfiguration von SPTools gemäß Ihren Vorgaben. Zudem bieten wir technische Beratung für Entwickler sowie Unterstützung bei der Anwendung und Konfiguration für Endkunden an."
    },
    {
      id: "faq-14",
      question: "Welche Support- und Wartungsleistungen bietet CCDS für SPTools an?",
      answer: "CCDS bietet professionelle Wartung und Support für SPTools auf Stundenbasis an. Der aktuelle Stundensatz beträgt 125,00 EUR netto."
    },
    {
      id: "faq-15",
      question: "Kann SPTools auch mit anderen Content-Management-Systemen als TYPO3 verwendet werden?",
      answer: "Derzeit ist SPTools speziell für die Integration mit TYPO3 entwickelt worden. Für andere Content-Management-Systeme können individuelle Lösungen auf Anfrage geprüft werden."
    },
    {
      id: "faq-16",
      question: "Gibt es eine Möglichkeit, SPTools vor dem Kauf zu testen?",
      answer: "Ja, Sie können eine kostenlose Demo anfordern, um SPTools live in Aktion zu erleben. Dabei zeigen wir Ihnen praxisnahe Anwendungsbeispiele – z. B. Dokumentenausgabe aus SharePoint, Terminbuchung mit Outlook-Kalender oder automatisierte Workflows.<br><br>Einfach Kontakt aufnehmen oder direkt einen Termin buchen: <a href='https://sptools.ccds.de' target='_blank' class='text-blue-500 hover:text-blue-700 font-medium'>sptools.ccds.de</a>"
    }
  ];

  useEffect(() => {
    if (!schemaRef.current) return;

    const schemaData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    schemaRef.current.textContent = JSON.stringify(schemaData);
  }, [faqData]);

  useEffect(() => {
    if (currentHash && faqData.length > 0) {
      const targetFaq = faqData.find(faq => faq.id === currentHash);
      if (targetFaq) {
        const itemId = `item-${faqData.indexOf(targetFaq) + 1}`;
        if (!openItems.includes(itemId)) {
          setOpenItems(prev => [...prev, itemId]);
          
          setTimeout(() => {
            const faqItem = document.querySelector(`[data-faq-id="${currentHash}"]`);
            if (faqItem) {
              const faqItemPosition = faqItem.getBoundingClientRect().top;
              const offsetPosition = faqItemPosition + window.pageYOffset - 120;
              
              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            }
          }, 300);
        }
      }
    }
  }, [currentHash, faqData, openItems]);

  const handleValueChange = (value: string) => {
    setOpenItems(prev => {
      if (prev.includes(value)) {
        return prev.filter(item => item !== value);
      }
      return [...prev, value];
    });
  };

  return (
    <section id="faq" ref={sectionRef} className="bg-white py-16">
      <script ref={schemaRef} type="application/ld+json"></script>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-dark mb-4">Häufig gestellte Fragen</h2>
            <h3 className="text-gray">Wichtige Informationen zu unseren TYPO3-Microsoft 365 Lösungen</h3>
          </div>
          
          <Accordion
            type="multiple" 
            className="w-full"
            value={openItems}
            onValueChange={(value) => {
              if (Array.isArray(value)) {
                setOpenItems(value);
              } else {
                handleValueChange(value);
              }
            }}
          >
            {faqData.map((faq, index) => (
              <AccordionItem
                key={faq.id}
                value={`item-${index + 1}`}
                className="mb-4 border border-gray-200 rounded-lg overflow-hidden"
                data-faq-id={faq.id}
              >
                <AccordionTrigger
                  id={faq.id}
                  className={cn(
                    "px-6 py-4 text-left text-lg font-medium bg-white hover:bg-gray-50",
                    "flex justify-between items-center w-full transition-all",
                    openItems.includes(`item-${index + 1}`) ? "text-blue" : "text-gray-dark"
                  )}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 text-gray-dark bg-white">
                  <div dangerouslySetInnerHTML={{ __html: faq.answer }}></div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
